/**
 * CustomerEngagementHub
 * -----------------------------------------------------------------------------
 * Marketing-Centre tab that turns the static "Customer Patterns" insight into
 * an actionable customer-retention workflow:
 *
 *   ┌──────────────────────────────────────────────────────────────────────┐
 *   │  4 clickable bucket cards (New · Returning · Inactive · Never)       │
 *   │  ↓ click any bucket                                                  │
 *   │  Drawer with the full list (paginated 500/page)                      │
 *   │   - Each row: ☑ name · contact · suburb · last-visit · lifetime $    │
 *   │   - Select all / quick-templates · "📧 Email selected"               │
 *   │     "💬 SMS selected"                                                │
 *   │  Compose modal: subject (email) / message body with token autosuggest│
 *   │     {{first_name}} {{last_visit}} {{rego}} ...                       │
 *   └──────────────────────────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import {
  Users, UserPlus, UserCheck, UserX, UserMinus, Wrench,
  Mail, MessageSquare, Loader2, Sparkles, Send, Filter, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BUCKETS = [
  { key: 'new',            label: 'New',            color: '#10b981', icon: UserPlus,  desc: 'First visit within the horizon' },
  { key: 'returning',      label: 'Returning',      color: '#3b82f6', icon: UserCheck, desc: 'Older customer who came back recently' },
  { key: 'inactive',       label: 'Inactive',       color: '#f59e0b', icon: UserX,     desc: 'Has invoices but not recently — re-engage' },
  { key: 'never_invoiced', label: 'Never Invoiced', color: '#94a3b8', icon: UserMinus, desc: 'On the books but never billed' },
  { key: 'service_due',    label: 'Vehicles Due',   color: '#ef4444', icon: Wrench,    desc: 'Vehicles overdue or due for service in next 30 days' },
];

const TEMPLATES = {
  inactive: [
    {
      label: 'We miss you (Inactive customers)',
      sms: "Hi {{first_name}}, it's been a while since we last saw {{vehicle_summary}}. We'd love to get you back in the workshop — call us at the shop or book online for 10% off your next service.",
      email_subject: "We miss you, {{first_name}} — 10% off your next service",
      email_body: "Hi {{first_name}},\n\nWe haven't seen {{vehicle_summary}} in a while and we'd love to take care of it again. As a thank-you for choosing us, your next service is 10% off if you book within the next 30 days.\n\nReply to this email or call us to book.\n\nThe Hoppers Service Centre team",
    },
    {
      label: 'Service due reminder',
      sms: "Hi {{first_name}}, {{vehicle_summary}} is due for a service. Book online or reply to this message to grab a slot.",
      email_subject: "{{vehicle_summary}} — service due",
      email_body: "Hi {{first_name}},\n\nOur records show {{vehicle_summary}} is due for a service. Book online or reply to schedule.\n\nThanks,\nHoppers Service Centre",
    },
  ],
  never_invoiced: [
    {
      label: 'Welcome — first-visit discount',
      sms: "Hi {{first_name}}, welcome to Hoppers Service Centre! Book your first service with us in the next 30 days and enjoy 15% off.",
      email_subject: 'Welcome to Hoppers Service Centre',
      email_body: "Hi {{first_name}},\n\nThanks for joining us. As a welcome gift, your first service is 15% off when booked in the next 30 days.\n\nLooking forward to taking care of your car,\nHoppers Service Centre",
    },
  ],
  new: [
    {
      label: 'Thank-you + review request',
      sms: "Thanks for choosing Hoppers Service Centre, {{first_name}}! If we got it right, please leave us a Google review: <link>",
      email_subject: "Thanks for choosing Hoppers Service Centre",
      email_body: "Hi {{first_name}},\n\nThank you for choosing us recently. If we got it right, we'd love a quick Google review — it really helps a small workshop like ours.\n\nReview link: <link>\n\nWith thanks,\nHoppers Service Centre",
    },
  ],
  returning: [
    {
      label: 'Loyalty thank-you',
      sms: "Hi {{first_name}}, thanks for being a loyal customer. We've added a free wash to your next service.",
      email_subject: "A small thank-you for sticking with us",
      email_body: "Hi {{first_name}},\n\nThank you for being a loyal customer. As a small thanks, your next service includes a complimentary wash on us.\n\nSee you soon,\nHoppers Service Centre",
    },
  ],
};

function currency(n) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(n || 0);
}
function fmtDate(s) {
  if (!s) return '—';
  return s.slice(0, 10);
}

export default function CustomerEngagementHub() {
  const [horizon, setHorizon] = useState(2);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Drawer state
  const [activeBucket, setActiveBucket] = useState(null);
  const [bucketList, setBucketList] = useState([]);
  const [bucketTotal, setBucketTotal] = useState(0);
  const [bucketLoading, setBucketLoading] = useState(false);
  const [hasContactOnly, setHasContactOnly] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Compose modal state
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeChannel, setComposeChannel] = useState('sms');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [sending, setSending] = useState(false);

  /* Load overview */
  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/reports/customer-patterns?horizon_years=${horizon}`, { credentials: 'include' });
        if (res.ok) setData(await res.json());
      } finally { setLoading(false); }
    })();
  }, [horizon]);

  /* Load a bucket when clicked */
  const openBucket = async (bucketKey) => {
    setActiveBucket(bucketKey);
    setBucketLoading(true);
    setSelectedIds(new Set());
    try {
      const url = `${BACKEND_URL}/api/reports/customer-patterns/bucket?bucket=${bucketKey}&horizon_years=${horizon}&limit=500&has_contact=${hasContactOnly}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.ok) {
        const json = await res.json();
        setBucketList(json.customers || []);
        setBucketTotal(json.total || 0);
      } else {
        toast.error('Failed to load bucket list');
      }
    } finally { setBucketLoading(false); }
  };

  // Re-fetch when has_contact toggle changes (only if drawer open)
  useEffect(() => {
    if (activeBucket) openBucket(activeBucket);
     
  }, [hasContactOnly]);

  const toggleAllVisible = (check) => {
    if (!check) { setSelectedIds(new Set()); return; }
    const ids = new Set(bucketList.map(c => c.customer_id));
    setSelectedIds(ids);
  };
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };

  /* Compose */
  const openCompose = (channel) => {
    if (selectedIds.size === 0) { toast.error('Select at least one customer'); return; }
    setComposeChannel(channel);
    setComposeOpen(true);
  };
  const loadTemplate = (t) => {
    setComposeBody(composeChannel === 'sms' ? t.sms : t.email_body);
    if (composeChannel === 'email') setComposeSubject(t.email_subject);
  };
  const handleSend = async () => {
    if (!composeBody.trim()) { toast.error('Message body required'); return; }
    if (composeChannel === 'email' && !composeSubject.trim()) { toast.error('Email subject required'); return; }
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketing/send`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_ids: Array.from(selectedIds),
          channel: composeChannel,
          subject: composeChannel === 'email' ? composeSubject : undefined,
          message: composeBody,
        }),
      });
      if (res.ok) {
        const d = await res.json();
        toast.success(d.message || `Sent: ${d.sent_count ?? selectedIds.size} · Failed: ${d.failed_count ?? 0}`);
        setComposeOpen(false);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to send campaign');
      }
    } finally { setSending(false); }
  };

  const pieData = useMemo(() => {
    if (!data?.pie) return [];
    return BUCKETS.map(b => ({ name: b.label, key: b.key, value: data.pie[b.key] || 0, color: b.color }));
  }, [data]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500" data-testid="ceh-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading customer engagement insights…
      </div>
    );
  }
  if (!data) {
    return <p className="text-sm text-slate-500" data-testid="ceh-empty">Customer engagement data is not yet available.</p>;
  }

  const { headline, pie } = data;
  const activeBucketMeta = BUCKETS.find(b => b.key === activeBucket);
  const allVisibleChecked = bucketList.length > 0 && bucketList.every(c => selectedIds.has(c.customer_id));

  return (
    <div className="space-y-6" data-testid="customer-engagement-hub">
      {/* Headline */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Total customers</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{(headline.total_customers || 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Customers with invoice</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{(headline.customers_with_invoice || 0).toLocaleString()}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Avg invoice value</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{currency(headline.avg_invoice_value)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-slate-500">Lifetime revenue</p>
          <p className="text-2xl font-bold text-slate-900 mt-1">{currency(headline.lifetime_revenue)}</p>
        </Card>
      </div>

      {/* Engagement split — clickable */}
      <Card className="p-6">
        <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
          <div>
            <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-emerald-600" /> Customer Engagement Split
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Click any bucket below to open the list — select customers, then email or SMS them in one click.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">Horizon</span>
            <select
              value={horizon}
              onChange={(e) => setHorizon(Number(e.target.value))}
              className="text-xs border border-slate-200 rounded px-2 py-1"
              data-testid="ceh-horizon"
            >
              {[1, 2, 3, 5].map(y => <option key={y} value={y}>{y} year{y === 1 ? '' : 's'}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1" style={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                     innerRadius={50} outerRadius={100} paddingAngle={2}>
                  {pieData.map((entry) => <Cell key={entry.key} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => v.toLocaleString()} />
                <Legend verticalAlign="bottom" />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BUCKETS.map(b => {
              const count = pie[b.key] || 0;
              const Icon = b.icon;
              return (
                <button
                  key={b.key}
                  type="button"
                  onClick={() => openBucket(b.key)}
                  data-testid={`ceh-bucket-${b.key}`}
                  className="text-left p-4 border-2 border-slate-200 rounded-lg hover:border-emerald-400 hover:shadow-sm transition-all bg-white group"
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg p-2" style={{ background: `${b.color}1a`, color: b.color }}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-slate-900">{b.label}</p>
                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 transition-colors" />
                      </div>
                      <p className="text-2xl font-bold mt-0.5" style={{ color: b.color }}>
                        {count.toLocaleString()}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">{b.desc}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Drawer for bucket list */}
      <Sheet open={!!activeBucket} onOpenChange={(o) => { if (!o) setActiveBucket(null); }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="ceh-drawer">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              {activeBucketMeta && <activeBucketMeta.icon className="h-5 w-5" style={{ color: activeBucketMeta.color }} />}
              {activeBucketMeta?.label} customers
              <Badge variant="secondary">{bucketTotal.toLocaleString()}</Badge>
            </SheetTitle>
            <SheetDescription>
              {activeBucketMeta?.desc} · Highest lifetime spend first.
              {bucketTotal > 500 && ' Showing first 500.'}
            </SheetDescription>
          </SheetHeader>

          {/* Controls */}
          <div className="flex items-center justify-between gap-2 mt-4 pb-3 border-b">
            <div className="flex items-center gap-2 text-xs">
              <Checkbox
                checked={hasContactOnly}
                onCheckedChange={(v) => setHasContactOnly(!!v)}
                data-testid="ceh-only-contactable"
              />
              <span className="text-slate-600">Contactable only (has mobile or email)</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Filter className="h-3.5 w-3.5" />
              {selectedIds.size} selected
            </div>
          </div>

          {bucketLoading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading list…
            </div>
          ) : (
            <div className="mt-3 border border-slate-200 rounded-lg max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left w-10">
                      <Checkbox
                        checked={allVisibleChecked}
                        onCheckedChange={(v) => toggleAllVisible(!!v)}
                        data-testid="ceh-select-all"
                      />
                    </th>
                    <th className="px-3 py-2 text-left">Customer</th>
                    <th className="px-3 py-2 text-left">Contact</th>
                    {activeBucket === 'service_due' ? (
                      <>
                        <th className="px-3 py-2 text-left">Vehicle</th>
                        <th className="px-3 py-2 text-right">Service due</th>
                      </>
                    ) : (
                      <>
                        <th className="px-3 py-2 text-left">Last visit</th>
                        <th className="px-3 py-2 text-right">Lifetime</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bucketList.length === 0 ? (
                    <tr><td colSpan={5} className="text-center text-slate-400 py-8">
                      {activeBucket === 'service_due'
                        ? 'No vehicles overdue or due for service yet.'
                        : `No customers in this bucket${hasContactOnly ? ' with a contact' : ''}.`}
                    </td></tr>
                  ) : bucketList.map(c => {
                    const checked = selectedIds.has(c.customer_id);
                    const contact = c.mobile || c.phone || c.email || '—';
                    return (
                      <tr key={c.customer_id + (c.rego || '')} className="border-b last:border-b-0 hover:bg-slate-50" data-testid={`ceh-row-${c.customer_id}`}>
                        <td className="px-3 py-2">
                          <Checkbox checked={checked} onCheckedChange={() => toggleOne(c.customer_id)} />
                        </td>
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-900">{c.display_name || c.name || 'Unnamed'}</p>
                          <p className="text-xs text-slate-500">{c.suburb || ''}</p>
                        </td>
                        <td className="px-3 py-2 tabular-nums text-slate-700">{contact}</td>
                        {activeBucket === 'service_due' ? (
                          <>
                            <td className="px-3 py-2 text-xs text-slate-700">
                              <span className="font-mono">{c.rego || '—'}</span>
                              <span className="text-slate-400 ml-2">{c.vehicle_summary || ''}</span>
                            </td>
                            <td className={`px-3 py-2 text-right text-xs font-semibold ${c.is_overdue ? 'text-rose-700' : 'text-amber-700'}`}>
                              {fmtDate(c.next_service_date)}
                              <span className="block text-[10px] font-normal">
                                {c.is_overdue ? 'overdue' : 'due soon'}
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(c.last_invoice_date)}</td>
                            <td className="px-3 py-2 text-right font-semibold tabular-nums">{currency(c.lifetime_total)}</td>
                          </>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Action bar */}
          <div className="sticky bottom-0 mt-4 -mx-6 px-6 py-3 border-t bg-white flex items-center justify-between">
            <p className="text-xs text-slate-500">{selectedIds.size} customer{selectedIds.size === 1 ? '' : 's'} selected</p>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline" disabled={selectedIds.size === 0}
                onClick={() => openCompose('sms')}
                data-testid="ceh-compose-sms"
              >
                <MessageSquare className="h-4 w-4 mr-1" /> SMS Selected
              </Button>
              <Button
                size="sm" disabled={selectedIds.size === 0}
                onClick={() => openCompose('email')}
                data-testid="ceh-compose-email"
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <Mail className="h-4 w-4 mr-1" /> Email Selected
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Compose Modal */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="max-w-2xl" data-testid="ceh-compose-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {composeChannel === 'sms' ? <MessageSquare className="h-5 w-5 text-blue-600" /> : <Mail className="h-5 w-5 text-emerald-600" />}
              Compose {composeChannel === 'sms' ? 'SMS' : 'Email'} to {selectedIds.size} customer{selectedIds.size === 1 ? '' : 's'}
            </DialogTitle>
            <DialogDescription>
              Tokens supported: <code className="bg-slate-100 px-1 rounded">{'{{first_name}}'}</code>{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{vehicle_summary}}'}</code>{' '}
              <code className="bg-slate-100 px-1 rounded">{'{{last_visit}}'}</code>
            </DialogDescription>
          </DialogHeader>

          {/* Templates */}
          {TEMPLATES[activeBucket]?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quick templates</p>
              <div className="flex flex-wrap gap-2">
                {TEMPLATES[activeBucket].map((t, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => loadTemplate(t)}
                    className="text-xs border border-slate-200 rounded-full px-3 py-1 hover:bg-emerald-50 hover:border-emerald-300"
                    data-testid={`ceh-template-${i}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Subject (email only) */}
          {composeChannel === 'email' && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Subject</label>
              <Input
                value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="e.g. We miss you, {{first_name}}"
                data-testid="ceh-compose-subject"
              />
            </div>
          )}

          {/* Body */}
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs font-semibold text-slate-600">Message</label>
              <span className="text-[10px] text-slate-400">
                {composeBody.length}/{composeChannel === 'sms' ? '160' : '4000'}
              </span>
            </div>
            <Textarea
              value={composeBody}
              onChange={(e) => setComposeBody(e.target.value)}
              rows={composeChannel === 'sms' ? 4 : 10}
              placeholder={composeChannel === 'sms'
                ? 'Hi {{first_name}}, …'
                : 'Hi {{first_name}},\n\n…\n\nThanks,\nHoppers Service Centre'}
              data-testid="ceh-compose-body"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)} data-testid="ceh-compose-cancel">
              Cancel
            </Button>
            <Button onClick={handleSend} disabled={sending} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ceh-compose-send">
              <Send className="h-4 w-4 mr-1" />
              {sending ? 'Sending…' : `Send to ${selectedIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
